<template>
  <div
    class="-upload-input -mt-2"
    :class="{ 'active-dropzone': activeDropZone }"
  >
    <div class="drop-area"
      @dragenter.prevent="toggleActive"
      @dragleave.prevent="toggleActive"
      @dragover.prevent
      @drop.prevent="droppedFile"
    >
    </div>
    <div class="-child-upload-input">
      <div class="-info has-text-center -w100" v-if="!response.status">
        <img src="@/assets/uploadDocument.png" alt="upload" class="upload-icon" />
        <p class="-thinking-text -mt-2 -text3" v-if="!isLoading">
          Arrastra tus archivos aquí o súbelos desde tu ordenador.
        </p>
        <div v-else class="-progress-container has-text-centered">
          <div class="is-flex -is-justify-content-center">
            <div class="-progress">
              <div :style="`width: ${percentage}%`"></div>
            </div>
          </div>
          <p class="-gray -text3">Cargando archivo...</p>
        </div>
      </div>

      <div v-else>
        <div v-if="response.success">
          <img src="@/assets/icon/verified.svg" alt="" />
          <p class="-text1 has-text-centered -text3">
            Archivo cargado: <strong>{{ file.name }}</strong>
          </p>
        </div>
        <div v-else>
          <img src="@/assets/icon/error.svg" alt="" />
          <p class="-text1 has-text-centered -text3">
            Lo sentimos, tu archivo no se ha cargado correctamente,<br />
            inténtalo de nuevo.
          </p>
        </div>
      </div>

      <div class="is-flex -is-justify-content-center -w100 -mt-2">
        <button
          class="-button button is-info is-outlined"
          :class="{ 'is-loading': isLoading }"
          type="button"
          @click="triggerFileInput"
        >
          <p>Subir archivo</p>
        </button>
        <input ref="fileInput" type="file" :accept="accept" @change="onFileChange" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
export default {
  name: 'UploadInput',
  props: {
    uploadFile: {
      type: Function,
      default: () => {}
    },
    accept: {
      type: String,
      default: 'image/*'
    },
    maxSize: {
      type: Number,
      default: 0
    },
    validateDimensions: {
      type: Boolean,
      default: false
    },
    maxWidth: {
      type: Number,
      default: 400
    },
    maxHeight: {
      type: Number,
      default: 160
    }
  },
  data () {
    return {
      isLoading: false,
      response: {
        status: false,
        success: false
      },
      percentage: 0,
      file: null,
      progressInterval: null,
      simulatedProgress: 0,
      activeDropZone: false,
      previewUrl: null
    }
  },
  methods: {
    ...mapActions(['addNewNotification']),
    triggerFileInput () {
      this.$refs.fileInput.click()
    },
    async onFileChange (event) {
      if (this.progressInterval) clearInterval(this.progressInterval)

      // Verificar que el evento tenga files y que haya al menos un archivo
      const file = event.target?.files?.[0] || event.dataTransfer?.files?.[0]
      if (!file) return

      if (this.validateDimensions) {
        const isValid = await this.validateImgDimensions(file)
        if (!isValid) return
      }

      if (this.maxSize && file.size > this.maxSize * 1024 * 1024) {
        this.addNewNotification({ type: 'warning', message: `El archivo debe tener un tamaño menor a ${this.maxSize} MB` })
        return
      }

      this.file = file

      this.isLoading = true
      this.response.status = false
      this.response.success = false
      this.percentage = 10
      this.simulatedProgress = 10
      this.$emit('file-selected', this.file)

      this.progressInterval = setInterval(() => {
        if (this.simulatedProgress < 100) {
          this.simulatedProgress += 10
          this.percentage = this.simulatedProgress
        } else {
          clearInterval(this.progressInterval)
          this.response.status = true
          this.response.success = true
          this.isLoading = false
        }
      }, 80)
    },
    droppedFile (evt) {
      this.toggleActive()
      this.onFileChange(evt)
    },
    toggleActive () {
      this.activeDropZone = !this.activeDropZone
    },
    validateImgDimensions (file) {
      return new Promise((resolve) => {
        const img = new Image()

        img.onload = () => {
          const isValid = img.width <= this.maxWidth && img.height <= this.maxHeight

          if (!isValid) {
            this.addNewNotification({ type: 'warning', message: `El archivo debe tener unas dimensiones máximas de ${this.maxWidth} × ${this.maxHeight} px` })
          }

          URL.revokeObjectURL(img.src)
          resolve(isValid)
        }

        img.onerror = () => {
          URL.revokeObjectURL(img.src)
          resolve(false)
        }

        img.src = URL.createObjectURL(file)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.has-text-centered {
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
}

.upload-icon {
  padding: 10px;
  width: 50px;
  height: 60px !important;
}

.-progress-container {
  margin-top: 1rem;
}

.-progress {
  width: 200px;
  height: 6px;
  background-color: #e0e0e0;
  border-radius: 3px;
  overflow: hidden;
}

.-progress > div {
  height: 100%;
  background-color: #00d1b2;
  transition: width 0.3s ease;
}

.-button {
  background: white;
  color: #4f73ff;
  border: 1px solid #4f73ff;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 2;
}

.-button:hover {
  background: #f8f9ff;
}

input[type="file"] {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}
.-upload-input {
  position: relative;
  z-index: 1;
}
.active-dropzone {
  box-shadow: inset 0 0.5em 1em -0.125em rgba(10, 10, 10, 0.1), 0 0px 0 1px rgba(10, 10, 10, 0.02);
  .drop-area {
    z-index: 3;
  }
}
.drop-area {
  width: 100%;
  height: 100%;
  position: absolute;
  z-index: 2;
}
.-child-upload-input {
  text-align: center;
}
</style>
